import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 100px 20px 60px 20px;
  background: radial-gradient(circle at top, #2c3e50 0%, #1b2838 40%, #0b1020 100%);
  color: white;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.85;
  margin-bottom: 30px;
`;

const Section = styled.section`
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 12px;
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  opacity: 0.9;
  white-space: pre-line;
`;

const PlannerLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 24px;
  margin-top: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const DateFieldWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: center;
`;

const DateIcon = styled.span`
  position: absolute;
  right: 35px;
  font-size: 0.95rem;
  opacity: 0.8;
  pointer-events: none;
`;

const Input = styled.input`
  flex: 1;
  min-width: 140px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.4);
  color: white;
  font-size: 0.95rem;

  &[type='date'] {
    padding-right: 32px; /* 留空間給右邊的小日曆 icon */
    cursor: pointer;
  }

  &:focus {
    outline: none;
    border-color: #4cd137;
    box-shadow: 0 0 0 1px rgba(76, 209, 55, 0.5);
  }
`;

const Select = styled.select`
  min-width: 120px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.4);
  color: white;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #e1b12c;
    box-shadow: 0 0 0 1px rgba(225, 177, 44, 0.5);
  }
`;

const AddButton = styled.button`
  padding: 8px 16px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #4cd137, #44bd32);
  color: #0b1020;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 14px rgba(76, 209, 55, 0.45);
  margin-top: 2px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(76, 209, 55, 0.6);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 10px rgba(76, 209, 55, 0.4);
  }
`;

const TasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const TaskCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const TaskInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskTitle = styled.div`
  font-size: 0.98rem;
  font-weight: 600;
  margin-bottom: 2px;
`;

const TaskMeta = styled.div`
  font-size: 0.8rem;
  opacity: 0.8;
`;

const MonsterLane = styled.div`
  height: 260px;
  border-radius: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  background: radial-gradient(circle at left, rgba(0, 184, 148, 0.4), transparent 55%),
              radial-gradient(circle at right, rgba(214, 48, 49, 0.4), transparent 55%);
  position: relative;
  overflow: hidden;
  padding: 12px;
  display: flex;
  flex-direction: column;
`;

const LaneLabel = styled.div`
  font-size: 0.8rem;
  opacity: 0.75;
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
`;

const LaneTrack = styled.div`
  flex: 1;
  position: relative;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1));
  overflow: hidden;
`;

const MonsterDot = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${props => `${props.offset}%`};
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
  border-radius: 999px;
  background: ${props => props.color};
  box-shadow: 0 0 18px ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => (props.size > 40 ? '1.2rem' : '0.9rem')};
`;

const MonsterLabel = styled.span`
  position: absolute;
  bottom: 6px;
  right: 10px;
  font-size: 0.7rem;
  opacity: 0.75;
`;

const TaskDefensePlanner = () => {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [priority, setPriority] = useState('medium');

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const handleAddTask = (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    setTasks((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: title.trim(),
        date: date || today,
        priority,
      },
    ]);

    setTitle('');
    setDate('');
    setPriority('medium');
  };

  const todayTasks = useMemo(
    () => tasks.filter((t) => t.date === today),
    [tasks, today]
  );

  const monsters = useMemo(
    () =>
      todayTasks.map((task, index) => {
        const baseOffset = 10 + index * 12;
        let size = 26;
        let color = '#00cec9';
        if (task.priority === 'high') {
          size = 54;
          color = '#e84118';
        } else if (task.priority === 'low') {
          size = 22;
          color = '#00b894';
        }
        return {
          id: task.id,
          label: task.title,
          size,
          color,
          offset: Math.min(baseOffset, 80),
        };
      }),
    [todayTasks]
  );

  return (
    <PageContainer>
      <ContentWrapper>
        <Title>Task Defense Planner</Title>
        <Subtitle>工作排程塔防工具（早期構想稿）</Subtitle>

        <Section>
          <SectionTitle>這頁暫時是 Concept 草稿</SectionTitle>
          <Text>
            這個專案的核心想法是：把「行事曆排程」跟「每日工作清單」做在一起，
            然後用塔防遊戲的方式把任務變成一隻一隻會入侵的怪獸。越重要、越急的任務，
            就像越大隻、越難打的怪獸，從畫面右邊慢慢走進來。
          </Text>
        </Section>

        <Section>
          <SectionTitle>第一版：文字版排程 + 今日怪物列表</SectionTitle>
          <PlannerLayout>
            <div>
              <Text>先從最小可行的「填表 + 今天的任務清單」開始，之後再慢慢長成真正的塔防關卡。</Text>
              <form onSubmit={handleAddTask}>
                <FormRow>
                  <Input
                    type="text"
                    placeholder="任務名稱（例如：寫報告、剪影片…）"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </FormRow>
                <FormRow>
                  <DateFieldWrapper>
                    <DateIcon>📅</DateIcon>
                    <Input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </DateFieldWrapper>
                  <Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="low">普通小怪（低）</option>
                    <option value="medium">精英怪（中）</option>
                    <option value="high">頭目級（高）</option>
                  </Select>
                  <AddButton type="submit">+ 生成一隻怪物</AddButton>
                </FormRow>
              </form>

              <TasksList>
                {tasks.length === 0 && (
                  <Text style={{ opacity: 0.7, fontSize: '0.9rem' }}>
                    還沒有任務，可以先隨便加幾個來感受一下節奏。
                  </Text>
                )}
                {tasks.map((task) => (
                  <TaskCard key={task.id}>
                    <TaskInfo>
                      <TaskTitle>{task.title}</TaskTitle>
                      <TaskMeta>
                        {task.date} ｜
                        {task.priority === 'high'
                          ? ' 頭目級'
                          : task.priority === 'medium'
                          ? ' 精英怪'
                          : ' 普通小怪'}
                      </TaskMeta>
                    </TaskInfo>
                  </TaskCard>
                ))}
              </TasksList>
            </div>

            <div>
              <MonsterLane>
                <LaneLabel>
                  <span>今天的路線（{today}）</span>
                  <span>{monsters.length} 隻怪物正在接近基地</span>
                </LaneLabel>
                <LaneTrack>
                  {monsters.map((monster) => (
                    <MonsterDot
                      key={monster.id}
                      size={monster.size}
                      color={monster.color}
                      offset={monster.offset}
                      title={monster.label}
                    >
                      🐉
                    </MonsterDot>
                  ))}
                  {monsters.length === 0 && (
                    <MonsterLabel>今天路線還很安全，沒有怪物接近。</MonsterLabel>
                  )}
                </LaneTrack>
                {monsters.length > 0 && (
                  <MonsterLabel>右側代表「快打到基地」的感覺。</MonsterLabel>
                )}
              </MonsterLane>
            </div>
          </PlannerLayout>
        </Section>

        <Section>
          <SectionTitle>接下來可以做的第一版功能</SectionTitle>
          <Text>
            1. 先做一個很簡單的「文字版排程」：可以新增任務、設定日期與重要度。
            {'\n'}2. 每天有一個「今日任務清單」區塊，會自動顯示今天到期或即將到期的任務。
            {'\n'}3. 做一個最小可行的「怪物列表」區塊，先用色塊／圓形代表不同強度的怪物，
            之後再慢慢加上動畫、塔防路線與攻擊效果。
          </Text>
        </Section>

        <Section>
          <SectionTitle>與其他工具的差異</SectionTitle>
          <Text>
            大部分排程工具都很實用，但視覺上偏嚴肅、也比較難完全貼合自己的腦內世界。
            Task Defense Planner 的重點是：讓你在整理工作的時候，
            感覺比較像是在「設計一場塔防關卡」，而不是在被待辦事項追著跑。
          </Text>
        </Section>
      </ContentWrapper>
    </PageContainer>
  );
};

export default TaskDefensePlanner;


